/**
 * Utilities for transforming monitoring execution data into editor-compatible format.
 */

import type { Run, Execution, NodeExecution, AgentIteration, ToolExecution } from '@/types/monitoring';
import type {
  EditorRunState,
  NodeRunData,
  NodeExecutionStatus,
  AgentIterationData,
  ToolExecutionData,
  NodePinData,
} from '@/types/editorRunData';

/**
 * Map monitoring status to editor status
 */
function mapStatus(status: string): NodeExecutionStatus {
  switch (status) {
    case 'succeeded':
      return 'succeeded';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    case 'cancelled':
      return 'cancelled';
    case 'running':
      return 'running';
    case 'pending':
    default:
      return 'pending';
  }
}

/**
 * Transform a tool execution from monitoring format to editor format
 */
function transformToolExecution(tool: ToolExecution): ToolExecutionData {
  const result: ToolExecutionData = {
    toolId: tool.toolId,
    toolName: tool.toolName,
    status: mapStatus(tool.status),
  };

  if (tool.actionName !== undefined) result.actionName = tool.actionName;
  if (tool.startTime !== undefined) result.startTime = tool.startTime;
  if (tool.endTime !== undefined) result.endTime = tool.endTime;
  if (tool.duration !== undefined) result.duration = tool.duration;
  if (tool.input !== undefined) result.input = tool.input;
  if (tool.output !== undefined) result.output = tool.output;

  return result;
}

/**
 * Transform an agent iteration from monitoring format to editor format
 */
function transformIteration(iteration: AgentIteration): AgentIterationData {
  const result: AgentIterationData = {
    index: iteration.index,
    status: mapStatus(iteration.status),
  };

  if (iteration.startTime !== undefined) result.startTime = iteration.startTime;
  if (iteration.endTime !== undefined) result.endTime = iteration.endTime;
  if (iteration.duration !== undefined) result.duration = iteration.duration;
  if (iteration.input !== undefined) result.input = iteration.input;
  if (iteration.output !== undefined) result.output = iteration.output;
  if (iteration.toolExecutions !== undefined) {
    result.toolExecutions = iteration.toolExecutions.map(transformToolExecution);
  }

  return result;
}

/**
 * Transform a node execution from monitoring format to editor format
 */
function transformNodeExecution(nodeExec: NodeExecution): NodeRunData {
  const result: NodeRunData = {
    nodeId: nodeExec.nodeId,
    nodeName: nodeExec.nodeName,
    nodeType: nodeExec.nodeType,
    status: mapStatus(nodeExec.status),
  };

  if (nodeExec.startTime !== undefined) result.startTime = nodeExec.startTime;
  if (nodeExec.endTime !== undefined) result.endTime = nodeExec.endTime;
  if (nodeExec.duration !== undefined) result.duration = nodeExec.duration;
  if (nodeExec.input !== undefined) result.input = nodeExec.input;
  if (nodeExec.output !== undefined) result.output = nodeExec.output;
  if (nodeExec.error !== undefined) result.error = nodeExec.error;
  if (nodeExec.iterations !== undefined) {
    result.iterations = nodeExec.iterations.map(transformIteration);
  }
  // Use iterationCount from API, or derive from iterations array length
  if (nodeExec.iterationCount !== undefined) {
    result.iterationCount = nodeExec.iterationCount;
  } else if (nodeExec.iterations !== undefined && nodeExec.iterations.length > 0) {
    result.iterationCount = nodeExec.iterations.length;
  }

  return result;
}

/**
 * Options for transforming execution data
 */
export interface TransformOptions {
  /**
   * Whether to auto-pin the trigger node output.
   * Defaults to true (like n8n behavior).
   */
  autoPinTrigger?: boolean;

  /**
   * The original workflow definition used during this execution.
   * Used for dirty node detection.
   */
  originalWorkflow?: unknown;
}

/**
 * Transform a Run + Execution into EditorRunState.
 *
 * This is the main entry point for loading execution data into the editor.
 *
 * @param run - The run from monitoring
 * @param execution - The specific execution to load (for agentic workflows, there may be multiple)
 * @param options - Transform options
 * @returns EditorRunState ready to be loaded into the editor
 */
export function transformExecutionToEditorState(
  run: Run,
  execution: Execution,
  options: TransformOptions = {}
): EditorRunState {
  const { autoPinTrigger = true, originalWorkflow } = options;

  // Transform all node executions
  const nodeRunData: Record<string, NodeRunData> = {};
  for (const nodeExec of execution.nodeExecutions) {
    nodeRunData[nodeExec.nodeId] = transformNodeExecution(nodeExec);
  }

  // Auto-pin trigger node output if enabled
  const pinData: Record<string, NodePinData> = {};
  if (autoPinTrigger) {
    const triggerNode = execution.nodeExecutions.find((n) => n.nodeId.startsWith('trigger-'));
    if (triggerNode?.output !== undefined) {
      pinData[triggerNode.nodeId] = {
        nodeId: triggerNode.nodeId,
        data: triggerNode.output,
        pinnedAt: new Date().toISOString(),
        source: 'execution',
      };
    }
  }

  const result: EditorRunState = {
    runId: run.id,
    executionId: execution.id,
    workflowId: run.workflowId,
    workflowName: run.workflowName,
    runStatus: run.status,
    runStartTime: run.startTime,
    loadedAt: new Date().toISOString(),
    nodeRunData,
    pinData,
  };

  if (run.endTime !== undefined) result.runEndTime = run.endTime;
  if (originalWorkflow !== undefined) result.originalWorkflow = originalWorkflow;

  return result;
}

/**
 * Get all node executions from a run, handling both agentic and standard workflows.
 *
 * For agentic workflows with multiple executions, this merges all node executions.
 * For standard workflows, this returns the single execution's node executions.
 *
 * @param run - The run to extract node executions from
 * @returns Array of all node executions
 */
export function getAllNodeExecutions(run: Run): NodeExecution[] {
  if (!run.executions || run.executions.length === 0) {
    return [];
  }

  // For agentic workflows, we might want to show the latest execution
  // or provide a way to select which execution to view
  // For now, use the first execution (which is typically the most recent)
  const firstExecution = run.executions[0];
  return firstExecution?.nodeExecutions ?? [];
}

/**
 * Get the default execution to load from a run.
 *
 * For standard workflows, this returns the single execution.
 * For agentic workflows, this returns the first (most recent) execution.
 *
 * @param run - The run to get the default execution from
 * @returns The default execution, or undefined if none
 */
export function getDefaultExecution(run: Run): Execution | undefined {
  if (!run.executions || run.executions.length === 0) {
    return undefined;
  }
  return run.executions[0];
}

/**
 * Check if a run has execution data that can be loaded into the editor.
 *
 * @param run - The run to check
 * @returns True if the run has loadable execution data
 */
export function canLoadIntoEditor(run: Run): boolean {
  // Must have at least one execution with node data
  const execution = getDefaultExecution(run);
  if (!execution) {
    return false;
  }

  // Must have at least one node execution
  if (execution.nodeExecutions.length === 0) {
    return false;
  }

  // For running workflows, allow loading if they have meaningful execution data
  // This handles conversational/agentic workflows that stay in "running" state,
  // as well as long-running standard workflows that the user wants to inspect.
  // The isAgentic flag from the API is not always reliable, so we allow any
  // running workflow with execution data to be debugged.
  return true;
}

/**
 * Get a summary of what data is available in an execution.
 *
 * @param execution - The execution to summarize
 * @returns Summary object
 */
export function getExecutionSummary(execution: Execution): {
  nodeCount: number;
  succeededCount: number;
  failedCount: number;
  skippedCount: number;
  hasAgentNodes: boolean;
  triggerNode: string | undefined;
} {
  const nodes = execution.nodeExecutions;

  return {
    nodeCount: nodes.length,
    succeededCount: nodes.filter((n) => n.status === 'succeeded').length,
    failedCount: nodes.filter((n) => n.status === 'failed').length,
    skippedCount: nodes.filter((n) => n.status === 'skipped').length,
    hasAgentNodes: nodes.some((n) => n.iterations && n.iterations.length > 0),
    triggerNode: nodes.find((n) => n.nodeId.startsWith('trigger-'))?.nodeName,
  };
}
